/**
 * The single evidence selection.
 *
 * There is exactly one selected piece of evidence at a time, held here rather than in
 * any of the views. That is the whole design: a chat clause, a table cell, a search
 * hit and a span list all *point at* the same panel instead of each growing a detail
 * view of its own. Four detail views would drift into four different ideas of what
 * counts as provenance, and an investigator would learn to trust whichever one they
 * happened to be looking at.
 */

import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from "react";
import type { Span } from "../lib/api";

export interface EvidenceRef {
  /** The block whose canonical text the offsets address. */
  readonly blockId: string;
  /** One or more spans in that block. Overlaps are merged when rendered. */
  readonly spans: readonly Span[];
  /** What the reader clicked — "E2", a column label, a search term. */
  readonly label?: string;
  /** Which surface it came from, shown so the panel is never context-free. */
  readonly origin?: string;
  /**
   * A second block and its spans, painted alongside the first.
   *
   * cooccurrence-graph: a `co_segment` edge's two mentions are, by construction, in
   * two different transcript segments (two different blocks); even a `co_document`
   * edge's pair frequently is. Rather than widening every existing single-block
   * caller (Chat, Search, Entities, Identities, the artifact table) to a multi-block
   * shape none of them need, this is an optional second half — present only when a
   * caller (the Links view) actually has two sides of a pair to show at once.
   */
  readonly secondaryBlockId?: string;
  readonly secondarySpans?: readonly Span[];
}

interface EvidenceState {
  readonly selected: EvidenceRef | null;
  /**
   * Whether the panel is being *shown* — which is a different question from whether
   * anything is selected, and only on a phone.
   *
   * On a wide screen the panel is a column that is always on screen, and this is
   * simply true whenever there is something to look at. Below app.css's phone
   * breakpoint the panel is an overlay, and the two facts come apart: dismissing the
   * sheet must not throw the selection away, or "take me back to the quote I was just
   * reading" would mean finding the citation marker and tapping it again.
   */
  readonly sheetOpen: boolean;
  select(ref: EvidenceRef): void;
  openSheet(): void;
  closeSheet(): void;
  clear(): void;
}

const EvidenceContext = createContext<EvidenceState | null>(null);

export function EvidenceProvider({ children }: { children: ReactNode }) {
  const [selected, setSelected] = useState<EvidenceRef | null>(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  // Selecting shows. A citation tap that surfaced the marker's evidence *somewhere*
  // and left the reader to go and find it would be a footnote, not evidence.
  const select = useCallback((ref: EvidenceRef) => {
    setSelected(ref);
    setSheetOpen(true);
  }, []);
  const openSheet = useCallback(() => setSheetOpen(true), []);
  const closeSheet = useCallback(() => setSheetOpen(false), []);
  const clear = useCallback(() => {
    setSelected(null);
    setSheetOpen(false);
  }, []);
  const value = useMemo(
    () => ({ selected, sheetOpen, select, openSheet, closeSheet, clear }),
    [selected, sheetOpen, select, openSheet, closeSheet, clear],
  );
  return <EvidenceContext.Provider value={value}>{children}</EvidenceContext.Provider>;
}

export function useEvidence(): EvidenceState {
  const value = useContext(EvidenceContext);
  if (!value) throw new Error("useEvidence must be used inside an EvidenceProvider");
  return value;
}
